/**
 * Rheina — Lenis smooth scroll (theme root)
 */
import Lenis from 'lenis';

/** @type {Lenis | null} */
let lenis = null;

/** @type {number | null} */
let rafId = null;

/** @type {boolean} */
let pausedByOverlay = false;

const OVERLAY_SELECTORS = [
  '[data-lenis-prevent]',
  'dialog[open]',
  '.rheina-header__mobile-panel',
  '.rheina-cart__panel',
  '.cart-drawer__dialog',
  '.search-modal__content',
  '.predictive-search-form__content',
  '.quick-add-modal__content',
  '.header__drawer',
  '.localization-form__list',
  '.facets-panel',
  '.overflow-list',
].join(',');

/**
 * @param {Node | null} node
 * @returns {boolean}
 */
function shouldPreventLenis(node) {
  if (!(node instanceof Element)) return false;
  return Boolean(node.closest(OVERLAY_SELECTORS));
}

function isScrollLocked() {
  return (
    document.documentElement.hasAttribute('scroll-lock') ||
    document.documentElement.classList.contains('rheina-header--drawer-open') ||
    document.documentElement.classList.contains('rheina-cart-open') ||
    document.body.style.position === 'fixed'
  );
}

function getHeaderOffset() {
  const value = getComputedStyle(document.body).getPropertyValue('--header-group-height').trim();
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function syncLenisWithLockState() {
  if (!lenis) return;

  if (isScrollLocked()) {
    pausedByOverlay = true;
    lenis.stop();
    return;
  }

  pausedByOverlay = false;
  lenis.start();
}

function handleAnchorClick(event) {
  if (!lenis) return;
  if (!(event.target instanceof Element)) return;
  if (event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const anchor = event.target.closest('a[href^="#"]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if (anchor.target === '_blank') return;

  const href = anchor.getAttribute('href');
  if (!href || href === '#') return;

  const id = decodeURIComponent(href.slice(1));
  if (!id) return;

  const target = document.getElementById(id);
  if (!target) return;

  event.preventDefault();
  lenis.scrollTo(target, {
    offset: id === 'MainContent' ? 0 : -getHeaderOffset(),
    duration: 1.1,
  });
}

function initLenis() {
  if (lenis) return;

  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  lenis = new Lenis({
    lerp: 0.12,
    wheelMultiplier: 0.9,
    smoothWheel: true,
    smoothTouch: false,
    syncTouch: false,
    autoRaf: false,
    prevent: shouldPreventLenis,
  });

  const raf = (time) => {
    lenis?.raf(time);
    rafId = requestAnimationFrame(raf);
  };
  rafId = requestAnimationFrame(raf);

  document.addEventListener('click', handleAnchorClick, { passive: false });
  window.addEventListener('resize', () => lenis?.resize(), { passive: true });
  document.addEventListener('shopify:section:load', () => lenis?.resize());

  const htmlObserver = new MutationObserver(() => {
    requestAnimationFrame(syncLenisWithLockState);
  });
  htmlObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['scroll-lock', 'class'],
  });

  const bodyObserver = new MutationObserver(() => {
    requestAnimationFrame(syncLenisWithLockState);
  });
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['style'],
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !lenis) return;
    pausedByOverlay = false;
    lenis.start();
    lenis.resize();
    syncLenisWithLockState();
  });

  window.RheinaLenis = {
    get: () => lenis,
    stop: () => lenis?.stop(),
    start: () => {
      pausedByOverlay = false;
      syncLenisWithLockState();
    },
    resize: () => lenis?.resize(),
    sync: syncLenisWithLockState,
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLenis, { once: true });
} else {
  initLenis();
}
